import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { emailHubService, ContactFormCategory } from '../services/emailHubService';
import { useSeoTags, buildWebPageJsonLd } from '../hooks/useSeoTags';
import './ContactPage.css';

const SUPPORT_EMAIL = 'support@loopyai.app';

// Re-export as named function for cleaner import surface
const submitContactForm = emailHubService.submitContactForm.bind(emailHubService);

interface FormState {
  name: string;
  email: string;
  category: ContactFormCategory | '';
  message: string;
}

interface FormErrors {
  name?: string;
  email?: string;
  message?: string;
}

type FieldName = keyof FormErrors;

const CATEGORIES: Array<{ value: ContactFormCategory; labelKey: string }> = [
  { value: 'general', labelKey: 'contact.categoryGeneral' },
  { value: 'partnership', labelKey: 'contact.categoryPartnership' },
  { value: 'creator_inquiry', labelKey: 'contact.categoryCreator' },
  { value: 'bug_report', labelKey: 'contact.categoryBug' },
  { value: 'feedback', labelKey: 'contact.categoryFeedback' },
  { value: 'billing', labelKey: 'contact.categoryBilling' },
  { value: 'press', labelKey: 'contact.categoryPress' },
];

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const SuccessIcon = () => (
  <svg
    className="contact-success-icon"
    width="64"
    height="64"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <circle cx="12" cy="12" r="10" />
    <polyline points="9 12 12 15 16 9" />
  </svg>
);

const SendIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <line x1="22" y1="2" x2="11" y2="13" />
    <polygon points="22 2 15 22 11 13 2 9 22 2" />
  </svg>
);

const Spinner = () => (
  <span className="contact-spinner" aria-hidden="true" />
);

const ContactPage: React.FC = () => {
  const { t } = useTranslation(['emailHub', 'common']);

  const [form, setForm] = useState<FormState>({
    name: '',
    email: '',
    category: '',
    message: '',
  });

  const [errors, setErrors] = useState<FormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const validateField = (field: FieldName, value: string): string | undefined => {
    if (field === 'name') {
      return value.trim() ? undefined : t('emailHub:contact.validation.nameRequired');
    }
    if (field === 'email') {
      return value.trim() && EMAIL_REGEX.test(value)
        ? undefined
        : t('emailHub:contact.validation.emailRequired');
    }
    if (field === 'message') {
      return value.trim().length >= 10
        ? undefined
        : t('emailHub:contact.validation.messageMin');
    }
    return undefined;
  };

  const validate = (): boolean => {
    const candidates: Record<FieldName, string | undefined> = {
      name: validateField('name', form.name),
      email: validateField('email', form.email),
      message: validateField('message', form.message),
    };
    const next: FormErrors = {};
    (Object.keys(candidates) as FieldName[]).forEach(k => {
      const err = candidates[k];
      if (err) next[k] = err;
    });
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>,
  ) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));

    // Clear the error for the field being edited (re-validate on blur, not while typing)
    if (name in errors) {
      setErrors(prev => ({ ...prev, [name]: undefined }));
    }
  };

  const handleBlur = (
    e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const { name, value } = e.target;
    if (name === 'name' || name === 'email' || name === 'message') {
      const err = validateField(name, value);
      setErrors(prev => ({ ...prev, [name]: err }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitError(null);

    if (!validate()) return;

    setIsSubmitting(true);

    try {
      await submitContactForm({
        name: form.name.trim(),
        email: form.email.trim(),
        message: form.message.trim(),
        category: form.category || undefined,
      });

      setIsSuccess(true);
    } catch (err: unknown) {
      const message =
        err instanceof Error
          ? err.message
          : t('emailHub:error');
      setSubmitError(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSendAnother = () => {
    setIsSuccess(false);
    setForm({ name: '', email: '', category: '', message: '' });
    setErrors({});
    setSubmitError(null);
  };

  // SEO for /contact — ContactPage schema is the standard schema.org type for
  // this page kind. Description + canonical pulled from i18n. The success
  // state reuses the same tags (same URL, just a different DOM region).
  const contactTitle = t('emailHub:contact.title');
  const contactDescription = t('emailHub:contact.subtitle');
  const seoTags = useSeoTags({
    title: contactTitle,
    description: contactDescription,
    canonical: '/contact',
    jsonLd: buildWebPageJsonLd({
      url: '/contact',
      name: contactTitle,
      description: contactDescription,
      breadcrumbs: [
        { name: 'Loopy', url: '/' },
        { name: contactTitle, url: '/contact' },
      ],
    }),
  });

  if (isSuccess) {
    return (
      <div className="contact-page">
        {seoTags}
        <div className="contact-wrap">
          <div className="contact-success">
            <SuccessIcon />
            <h2 className="contact-success-title">
              {t('emailHub:contact.successTitle')}
            </h2>
            <p className="contact-success-message">
              {t('emailHub:contact.successMessage')}
            </p>
            <div className="contact-success-actions">
              <button
                type="button"
                className="contact-btn-primary"
                onClick={handleSendAnother}
              >
                {t('emailHub:contact.sendAnother')}
              </button>
              <div className="contact-success-secondary">
                <Link to="/faq" className="contact-success-link">
                  {t('emailHub:contact.successFaq')}
                </Link>
                <span className="contact-success-sep" aria-hidden="true">·</span>
                <Link to="/" className="contact-success-link">
                  {t('emailHub:contact.successHome')}
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="contact-page">
      {seoTags}
      <div className="contact-wrap">
        <header className="contact-hero">
          <h1 className="contact-hero__title">
            {t('emailHub:contact.title')}
          </h1>
          <p className="contact-hero__subtitle">
            {t('emailHub:contact.subtitle')}
          </p>
        </header>

        <aside className="contact-helpline">
          <p className="contact-helpline__faq">
            {t('emailHub:contact.helpFaqBefore')}
            <Link to="/faq" className="contact-helpline__link">
              {t('emailHub:contact.helpFaqLink')}
            </Link>
            {t('emailHub:contact.helpFaqAfter')}
          </p>
          <p className="contact-helpline__meta">
            <span className="contact-helpline__time">
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <circle cx="12" cy="12" r="9" />
                <polyline points="12 7 12 12 15 14" />
              </svg>
              {t('emailHub:contact.responseTime')}
            </span>
            <span className="contact-helpline__sep" aria-hidden="true">·</span>
            <a href={`mailto:${SUPPORT_EMAIL}`} className="contact-helpline__link">
              {SUPPORT_EMAIL}
            </a>
          </p>
        </aside>

        <div className="contact-card">
          {submitError && (
            <div className="contact-error-banner" role="alert">
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <circle cx="12" cy="12" r="10" />
                <line x1="12" y1="8" x2="12" y2="12" />
                <line x1="12" y1="16" x2="12.01" y2="16" />
              </svg>
              <span>{submitError}</span>
              <button
                type="button"
                className="contact-error-dismiss"
                onClick={() => setSubmitError(null)}
                aria-label={t('common:close')}
              >
                &times;
              </button>
            </div>
          )}

          <form
            className="contact-form"
            onSubmit={handleSubmit}
            noValidate
          >
            {/* Name */}
            <div className={`contact-field${errors.name ? ' contact-field--error' : ''}`}>
              <label className="contact-label" htmlFor="contact-name">
                {t('emailHub:contact.name')}
                <span className="contact-required" aria-hidden="true"> *</span>
              </label>
              <input
                id="contact-name"
                type="text"
                name="name"
                className="contact-input"
                placeholder={t('emailHub:contact.namePlaceholder')}
                value={form.name}
                onChange={handleChange}
                onBlur={handleBlur}
                autoComplete="name"
                disabled={isSubmitting}
              />
              {errors.name && (
                <span className="contact-field-error" role="alert">
                  {errors.name}
                </span>
              )}
            </div>

            {/* Email */}
            <div className={`contact-field${errors.email ? ' contact-field--error' : ''}`}>
              <label className="contact-label" htmlFor="contact-email">
                {t('emailHub:contact.email')}
                <span className="contact-required" aria-hidden="true"> *</span>
              </label>
              <input
                id="contact-email"
                type="email"
                name="email"
                className="contact-input"
                placeholder={t('emailHub:contact.emailPlaceholder')}
                value={form.email}
                onChange={handleChange}
                onBlur={handleBlur}
                autoComplete="email"
                disabled={isSubmitting}
              />
              {errors.email && (
                <span className="contact-field-error" role="alert">
                  {errors.email}
                </span>
              )}
            </div>

            {/* Category */}
            <div className="contact-field">
              <label className="contact-label" htmlFor="contact-category">
                {t('emailHub:contact.category')}
              </label>
              <select
                id="contact-category"
                name="category"
                className="contact-select"
                value={form.category}
                onChange={handleChange}
                disabled={isSubmitting}
              >
                <option value="">{t('emailHub:contact.categoryPlaceholder')}</option>
                {CATEGORIES.map(opt => (
                  <option key={`${opt.value}-${opt.labelKey}`} value={opt.value}>
                    {t(`emailHub:${opt.labelKey}`)}
                  </option>
                ))}
              </select>
            </div>

            {/* Message */}
            <div className={`contact-field${errors.message ? ' contact-field--error' : ''}`}>
              <label className="contact-label" htmlFor="contact-message">
                {t('emailHub:contact.message')}
                <span className="contact-required" aria-hidden="true"> *</span>
              </label>
              <textarea
                id="contact-message"
                name="message"
                className="contact-textarea"
                placeholder={t('emailHub:contact.messagePlaceholder')}
                value={form.message}
                onChange={handleChange}
                onBlur={handleBlur}
                rows={6}
                disabled={isSubmitting}
              />
              {errors.message && (
                <span className="contact-field-error" role="alert">
                  {errors.message}
                </span>
              )}
            </div>

            {/* Submit */}
            <button
              type="submit"
              className="contact-btn-submit"
              disabled={isSubmitting}
            >
              {isSubmitting ? <Spinner /> : <SendIcon />}
              <span>
                {isSubmitting
                  ? t('emailHub:contact.submitting')
                  : t('emailHub:contact.submit')}
              </span>
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};


export default ContactPage;
